/**
 * AI agent seat hooks.
 *
 * Pairs with BE routes (all gated on `manage-agents`):
 *   POST /api/agents/provider-check   → prove a key; stores nothing
 *   GET  /api/agents                  → list
 *   POST /api/agents                  → create
 *   GET  /api/agents/{id}             → one
 *   PATCH /api/agents/{id}            → edit (a provider/base-URL change needs a key)
 *   PUT  /api/agents/{id}/key         → rotate the provider key
 *   POST /api/agents/{id}/deactivate | /reactivate
 *
 * The API key is request-only material. It is never part of a query key or
 * of any cached data (responses carry `keyLast4` and nothing else), and the
 * three mutations that carry it opt out of mutation-cache retention with
 * `gcTime: 0` so the variables don't linger in memory after the call.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import { AGENT_MODELS_QUERY_KEY } from '@/features/agents/use-agent-models'
import { CARD_LIST_QUERY_ROOT } from '@/features/board/components/list/use-card-list'
import { CONVERSATIONS_QUERY_KEY } from '@/features/chat/hooks/use-conversations'
import { DASHBOARD_QUERY_KEY } from '@/features/dashboard/use-dashboard'

import { ORGANIZATION_QUERY_KEY } from '../use-organization'
import { TENANT_USERS_QUERY_KEY } from '../use-tenant-users'

import type {
  Agent,
  CreateAgentInput,
  ProviderCheckInput,
  RotateAgentKeyInput,
  UpdateAgentInput,
} from './types'

export const AGENTS_QUERY_KEY = ['tenant-agents'] as const
export const agentQueryKey = (id: string) => [...AGENTS_QUERY_KEY, id] as const
export const agentPriceQueryKey = (id: string) => [...agentQueryKey(id), 'price'] as const

/**
 * Agents also render as rows in the Users table and hold a paid seat, so
 * every write refreshes those two alongside the agents themselves. The
 * agents prefix covers the list and every per-agent detail.
 */
const invalidateAgentSurfaces = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
  // Every AI tag reads the model from here, so a model change, a rotated key
  // or a (de)activation must reach them without waiting out the stale time.
  queryClient.invalidateQueries({ queryKey: AGENT_MODELS_QUERY_KEY })
}

/**
 * An agent's photo is denormalised into every payload that names the agent:
 * boards and master (card assignee + project members), card lists, chat
 * conversations and their members, and user pickers. The CDN URL changes on
 * every upload, so each of those caches would otherwise keep drawing the old
 * image (or initials) until a reload. Prefix keys, so every per-project /
 * per-conversation entry is covered.
 */
const invalidateAvatarSurfaces = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['board'] })
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: CARD_LIST_QUERY_ROOT })
  queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: ['conversation'] })
  queryClient.invalidateQueries({ queryKey: ['conversation-messages'] })
  queryClient.invalidateQueries({ queryKey: ['thread-messages'] })
  queryClient.invalidateQueries({ queryKey: ['comments'] })
  queryClient.invalidateQueries({ queryKey: ['user-search'] })
}

export function useAgents(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient<{ data: { agents: readonly Agent[] } }>('/api/agents')
      return res.data.agents
    },
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useAgent(id: string | null) {
  return useQuery({
    queryKey: agentQueryKey(id ?? ''),
    queryFn: async () => {
      const res = await apiClient<{ data: Agent }>(`/api/agents/${id}`)
      return res.data
    },
    enabled: id != null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/** Resolves the model ids to pick from, or `null` when the endpoint has no list. */
export function useCheckProviderKey() {
  return useMutation<readonly string[] | null, Error, ProviderCheckInput>({
    gcTime: 0,
    mutationFn: async (input) => {
      const res = await apiClient<{ data: { models: readonly string[] | null } }>(
        '/api/agents/provider-check',
        { method: 'POST', body: input },
      )
      return res.data.models
    },
  })
}

export function useCreateAgent() {
  const queryClient = useQueryClient()
  return useMutation<Agent, Error, CreateAgentInput>({
    gcTime: 0,
    mutationFn: async (input) => {
      const res = await apiClient<{ data: Agent }>('/api/agents', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
    onSuccess: () => invalidateAgentSurfaces(queryClient),
  })
}

export function useUpdateAgent() {
  const queryClient = useQueryClient()
  return useMutation<Agent, Error, { id: string; input: UpdateAgentInput }>({
    mutationFn: async ({ id, input }) => {
      const res = await apiClient<{ data: Agent }>(`/api/agents/${id}`, {
        method: 'PATCH',
        body: input,
      })
      return res.data
    },
    onSuccess: (_agent, variables) => {
      invalidateAgentSurfaces(queryClient)
      // A custom price belongs to one model, so a new model has a new price.
      if (variables.input.model !== undefined) {
        queryClient.invalidateQueries({ queryKey: agentPriceQueryKey(variables.id) })
      }
      if (variables.input.avatarUrl !== undefined) invalidateAvatarSurfaces(queryClient)
    },
  })
}

export function useRotateAgentKey() {
  const queryClient = useQueryClient()
  return useMutation<Agent, Error, { id: string; input: RotateAgentKeyInput }>({
    gcTime: 0,
    mutationFn: async ({ id, input }) => {
      const res = await apiClient<{ data: Agent }>(`/api/agents/${id}/key`, {
        method: 'PUT',
        body: input,
      })
      return res.data
    },
    onSuccess: (_agent, variables) => {
      invalidateAgentSurfaces(queryClient)
      queryClient.invalidateQueries({ queryKey: agentPriceQueryKey(variables.id) })
    },
  })
}

export function useSetAgentActive() {
  const queryClient = useQueryClient()
  return useMutation<Agent, Error, { id: string; nextActive: boolean }>({
    mutationFn: async ({ id, nextActive }) => {
      const res = await apiClient<{ data: Agent }>(
        `/api/agents/${id}/${nextActive ? 'reactivate' : 'deactivate'}`,
        { method: 'POST' },
      )
      return res.data
    },
    onSuccess: () => invalidateAgentSurfaces(queryClient),
  })
}
